import { CSSProperties } from "react"
const cjkLength = require('cjk-length').default

interface PoemLineStyle extends CSSProperties {
  fontFamily: string;
  fontSize: string;
  lineHeight: number;
  letterSpacing: string;
  color: string;
  paddingTop: string;
  paddingBottom: string;
  maxWidth: string;
  width: string;
}

type renderRect = {
  width: number;
  height: number;
}


interface CompositionConfig {
  wrap: boolean;
  scale: number;
  noAuthorLabel?: boolean;
  mainFont?: string;
  titleFont?: string;
  titleCjkLength: number;
  // TitlePadding: number;
  // titleLineHeightPX: CSSProperties['lineHeight'];
  // titleFontSize: number;
  // titleFontFamily: CSSProperties['fontFamily'];
  maxWidth: number;
  cardStyle: {
    'paddingTop': number;
    'paddingBottom': number;
    'paddingLeft': number;
    'paddingRight': number
  };
  textMaxWidth: number;
  estimateRenderRect: (props: {
    lines: string[];
    title: string;
    prefaceLines: string[];
    subtitle?: string;
    settings: CompositionConfig;
    location?: string;
    date?: string;
    translators?: string | null;
  }) => renderRect,
  innerEmptyLineHeightPX: string;
  innerLineStyle: PoemLineStyle;
  titleStyle: CSSProperties & {
    lineHeight: number;
    paddingTop: string;
    paddingBottom: string;
    marginBottom: string;
    fontSize: string;
  };
  subtitleStyle: CSSProperties & {
    lineHeight: number;
    fontSize: string;
    marginBottom: string;
    letterSpacing: string;
  };
  prefaceStyle: CSSProperties & {
    fontFamily: string;
    lineHeight: number;
    fontSize: string;
    letterSpacing: string;
  };
  dateLocationStyle: CSSProperties & {
    lineHeight: number;
    fontSize: string;
    letterSpacing: string;
    marginBottom: string;
  };
  authorStyle: CSSProperties & {
    fontSize: string;
  };
}

interface Configs {
  [key: string]: (option: { wrap: boolean, noAuthorLabel?: boolean, scale?: number, mainFont?: string, titleFont?: string }) => CompositionConfig
}

interface PureProps {
  poet: string;
  poem: string;
  title: string;
  // Allow null to be passed from external callers; components will normalize to ''
  preface?: string | null;
  subtitle?: string | null;
  poetHead?: string;
  compositionId: string;
  poetVerified?: boolean;
  location?: string;
  date?: string;
  translators: string | null;
  config: Pick<CompositionConfig, 'scale' | 'wrap' | 'noAuthorLabel' | 'mainFont' | 'titleFont'> & Partial<CompositionConfig>;
}

interface NftProps extends PureProps {
  time: string;
  appCode: string;
  collectorHead: string;
  collector: string;
  content: PureProps;
}

export type { CompositionConfig, PoemLineStyle as PoemStyle, PureProps, NftProps }

export const serif = `Cardo, 'Source Han Serif SC', 'Source Han Serif', "NotoColorEmoji", serif`
export const sansSerif = `HankenGrotesk, "Source Han Sans SC", "Source Sans Pro", "Noto Sans SC", "Noto Sans TC", "Noto Sans KR", "Noto Sans JP", "Noto Sans", "NotoColorEmoji", sans-serif`

export const compositions: Configs = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pure({ wrap = true, scale = 2, noAuthorLabel = false, mainFont, titleFont }): CompositionConfig {
    const PIC_MAX_WIDTH = 750
    const OUTTER_PADDING_X = 96
    const TEXT_MAX_WIDTH = PIC_MAX_WIDTH - OUTTER_PADDING_X * 2
    const BASE_FONT_SIZE = 32
    const titleFontSize = 60 // BASE_FONT_SIZE * 1.7143
    const baseFont = mainFont || serif
    return {
      wrap,
      scale,
      titleCjkLength: 24, // How many characters a line of title can contain
      titleStyle: {
        fontFamily: titleFont || serif,
        fontSize: titleFontSize + 'px',
        fontWeight: 600, // semi bold
        lineHeight: 92 / 64,
        paddingBottom: '0',
        paddingTop: '0',
        marginBottom: '58px',
        color: '#373737',
      },
      maxWidth: PIC_MAX_WIDTH,
      cardStyle: {
        paddingTop: 76,
        paddingBottom: 0,
        paddingLeft: OUTTER_PADDING_X,
        paddingRight: OUTTER_PADDING_X,
      },
      textMaxWidth: TEXT_MAX_WIDTH,
      subtitleStyle: {
        fontFamily: sansSerif,
        fontSize: BASE_FONT_SIZE + 'px',
        fontWeight: 600,
        lineHeight: 40 / BASE_FONT_SIZE,
        marginBottom: '36px',
        color: '#373737',
        letterSpacing: '0.005em',
      },
      prefaceStyle: {
        paddingLeft: '2em',
        fontFamily: baseFont,
        lineHeight: 40 / (BASE_FONT_SIZE * (12 / 14)),
        fontSize: BASE_FONT_SIZE + 'px',
        color: '#808080',
        letterSpacing: '0.005em',
      },
      dateLocationStyle: {
        fontFamily: baseFont,
        fontSize: (BASE_FONT_SIZE - 4) + 'px',
        letterSpacing: '0.005em',
        lineHeight: 1.46,
        color: '#808080',
        marginBottom: '24px'
      },
      authorStyle: {
        fontFamily: baseFont,
        fontSize: (BASE_FONT_SIZE - 4) + 'px',
        fontWeight: 500,
      },
      innerEmptyLineHeightPX: 1.4 * BASE_FONT_SIZE + 'px',
      innerLineStyle: { // Style for poem lines
        paddingTop: 0.14 * BASE_FONT_SIZE + 'px',
        paddingBottom: 0.14 * BASE_FONT_SIZE + 'px',
        fontFamily: baseFont,
        fontSize: BASE_FONT_SIZE + 'px',
        fontWeight: 400,
        lineHeight: 1.46,
        letterSpacing: '0.2px',
        color: '#181818',
        boxSizing: 'border-box',
        maxWidth: wrap ? TEXT_MAX_WIDTH + 'px' : 'fit-content',
        width: TEXT_MAX_WIDTH + 'px',
      },
      estimateRenderRect(props) {
        const { lines, title, prefaceLines, subtitle = '', settings } = props
        const subtitleHeight = subtitle && subtitle.length
          ? estimateHeight({
            lines: [subtitle],
            textMaxWidth: settings.textMaxWidth,
            fontWidth: parseFloat(settings.subtitleStyle.fontSize),
            spacing: parseFloat(settings.subtitleStyle.letterSpacing),
            lineHeight: settings.subtitleStyle.lineHeight
          }) + parseFloat(settings.subtitleStyle.marginBottom)
          : 0
        const prefaceHeight = prefaceLines && prefaceLines.length
          ? estimateHeight({
            lines: prefaceLines,
            textMaxWidth: settings.textMaxWidth,
            fontWidth: parseFloat(settings.prefaceStyle.fontSize),
            spacing: parseFloat(settings.prefaceStyle.letterSpacing),
            lineHeight: settings.prefaceStyle.lineHeight
          })
          : 0
        const prefaceBottom = (subtitleHeight || prefaceHeight ? 58 : 0)
        // console.log({ subtitleHeight, prefaceHeight })

        const baseRect: renderRect = estimateRenderRect(lines, title, settings)
        console.log('baseRect: ', baseRect)

        const dateLocationHeight = (subtitleHeight || prefaceHeight
          ? estimateHeight({
            lines: [props.location + ' ' + props.date],
            textMaxWidth: settings.textMaxWidth,
            fontWidth: parseFloat(settings.dateLocationStyle.fontSize),
            spacing: parseFloat(settings.dateLocationStyle.letterSpacing),
            lineHeight: settings.dateLocationStyle.lineHeight
          }) + parseFloat(settings.dateLocationStyle.marginBottom)
          : 0)

        const authorHeight = 28
        const translatorsHeight = props.translators ? (16 + 28) : 0

        const cardBottomHeight = 130 + 160

        const total = baseRect.height + subtitleHeight + prefaceHeight + prefaceBottom + dateLocationHeight + authorHeight + translatorsHeight + 66 + cardBottomHeight
        // Make the height even to reduce export anti-alias hairlines
        const evenHeight = total % 2 === 0 ? total : total + 1
        return {
          width: baseRect.width,
          height: evenHeight,
        }
      }
    }
  },

  nft({ wrap = true, scale = 3 }): CompositionConfig {
    const PIC_MAX_WIDTH = 342
    const OUTTER_PADDING = 24 * 2
    const OUTTERY_PADDING = 8 * 2
    const TEXT_MAX_WIDTH = PIC_MAX_WIDTH - OUTTER_PADDING * 1.5
    const BASE_FONT_SIZE = 14
    return {
      wrap: true,
      scale,
      titleCjkLength: 24,
      // TitleFontSize: 14,
      // titleLineHeightPX: `${1.25 * 14}px`,
      // titleFontFamily: serif,
      // titlePadding: (8 + 8),
      titleStyle: {
        fontFamily: serif,
        fontSize: 14 + 'px',
        lineHeight: 1.25,
        paddingBottom: (8 + 8) + 'px',
        paddingTop: '0',
        marginBottom: 58 + 'px',
      },
      subtitleStyle: {
        fontFamily: '"Source Han Sans SC", "Source Sans Pro", "Twitter Color Emoji", sans-serif',
        fontSize: BASE_FONT_SIZE + 'px',
        lineHeight: 17.6 / 14,
        letterSpacing: '0.005em',
        marginBottom: '0',
      },
      dateLocationStyle: {
        fontFamily: sansSerif,
        fontSize: '28px',
        letterSpacing: '0.005em',
        lineHeight: 1.46,
        color: '#808080',
        marginBottom: '24px'
      },
      authorStyle: {
        fontSize: BASE_FONT_SIZE + 'px',
      },
      prefaceStyle: {
        fontFamily: sansSerif,
        marginTop: 8 + 'px',
        marginBottom: 8 + 'px',
        fontSize: '28px',
        lineHeight: 40 / (BASE_FONT_SIZE * (12 / 14)),
        letterSpacing: '0.005em',
      },
      maxWidth: PIC_MAX_WIDTH,
      cardStyle: {
        paddingTop: OUTTERY_PADDING,
        paddingBottom: OUTTERY_PADDING,
        paddingLeft: OUTTER_PADDING,
        paddingRight: OUTTER_PADDING,
      },
      textMaxWidth: TEXT_MAX_WIDTH,
      innerEmptyLineHeightPX: 1.667 * BASE_FONT_SIZE + 'px',
      innerLineStyle: { // Style for lines wrapper
        fontFamily: '"Source Sans Pro", "Source Han Sans SC","Twitter Color Emoji", serif',
        fontSize: BASE_FONT_SIZE + 'px',
        fontWeight: 400,
        lineHeight: 1.667,
        letterSpacing: '0.005em',
        color: '#808080',
        boxSizing: 'border-box',
        maxWidth: wrap ? TEXT_MAX_WIDTH + 'px' : 'fit-content',
        width: TEXT_MAX_WIDTH + 'px',
        paddingTop: '0',
        paddingBottom: '0',
      },
      estimateRenderRect(props) {
        const { lines, title, prefaceLines, subtitle = '', settings } = props
        // Normalize null -> '' to safely call .length
        const safeSubtitle = subtitle || ''
        const baseRect: renderRect = estimateRenderRect(lines, title, settings)
        const subtitleHeight = safeSubtitle.length
          ? (8 * 2 + estimateHeight({
            lines: [safeSubtitle],
            textMaxWidth: settings.textMaxWidth,
            fontWidth: 14,
            spacing: 0.005,
            lineHeight: 1.25
          }))
          : 0
        const prefaceHeight = prefaceLines.length
          ? (8 * 2 + estimateHeight({
            lines: prefaceLines,
            textMaxWidth: settings.textMaxWidth,
            fontWidth: 12,
            spacing: 0.005,
            lineHeight: 1.667
          }))
          : 0
        console.log({ subtitleHeight, prefaceHeight })
        return {
          width: baseRect.width,
          height: baseRect.height + 404 + subtitleHeight + prefaceHeight + (subtitleHeight || prefaceHeight ? 8 * 2 : 0)
        }
      }
    }
  }
}

// estimate height of poem + title + card padding
export function estimateRenderRect(lines: string[], title: string, settings: CompositionConfig): { width: number, height: number } {
  const { cardStyle, titleCjkLength, innerLineStyle, innerEmptyLineHeightPX, titleStyle, textMaxWidth: allowWidth } = settings
  const fontWidth = parseFloat(innerLineStyle.fontSize)
  const spacing = parseFloat(innerLineStyle.letterSpacing)
  const innerLineHeight = innerLineStyle.lineHeight
  const emptyLineHeight = parseFloat(String(innerEmptyLineHeightPX))
  const innerLinePadding = parseFloat(String(innerLineStyle.paddingBottom)) + parseFloat(String(innerLineStyle.paddingTop))

  // Const ctx = getCanvasCtx(style.fontSize + ' "' + style.fontFamily + '"')
  let maxWidth = 0

  // Calculate visual width of characters.
  // ref: https://www.php.net/manual/en/function.mb-strwidth.php
  // ref: https://github.com/msikma/zenkaku-string
  // ref: https://www.npmjs.com/package/cjk-length
  // ref: https://github.com/komagata/eastasianwidth
  const titleLength = cjkLength(title)
  const titleLineHeight = titleStyle.lineHeight * parseFloat(titleStyle.fontSize)
  const titlePadding = parseFloat(String(titleStyle.paddingTop)) + parseFloat(String(titleStyle.paddingBottom))
  const titleMargin: number = parseFloat(String(titleStyle.marginBottom))

  let titleHeight = title === '' ? 0 : titlePadding + titleLineHeight + titleMargin
  // console.log({ titleLineHeight, titleLength, titleCjkLength, titleLines: titleLength / titleCjkLength, extraLines: Math.ceil(titleLength / titleCjkLength) - 1 })
  if (titleLength > titleCjkLength) {
    titleHeight += (Math.ceil(titleLength / titleCjkLength) - 1) * titleLineHeight
  }
  console.log({ titleCjkLen: titleLength, titleHeight })

  let height = 0
  const isLinesEmpty = lines.join() === ''
  if (!isLinesEmpty) {
    height = lines.reduce((prev, current) => {
      // Console.log(current, current.length, cjkLength(current))
      let width = cjkLength(current) * fontWidth / 2
      width *= (1.0 + spacing)
      maxWidth = width > maxWidth ? width : maxWidth

      const height = /^\s*$/.test(current)
        ? emptyLineHeight
        : Math.ceil(width / allowWidth) * fontWidth * innerLineHeight + innerLinePadding
      return prev + height
    }, height)
  }
  console.log('poem main text height= ', height)

  height += cardStyle.paddingTop + cardStyle.paddingBottom
    + titleHeight

  // Have to ceil float to int here because puppetter only accept int as width/height
  return { width: Math.ceil(maxWidth), height: Math.ceil(height) }
}

function estimateHeight(args: { lines: string[], textMaxWidth: number, fontWidth: number, spacing: number, lineHeight: number }): number {
  const { lines, textMaxWidth, fontWidth, spacing, lineHeight } = args
  return Math.ceil(lines.reduce((prev, current) => {
    const currentWidth = cjkLength(current) * fontWidth / 2 * (1 + spacing)
    return prev + Math.ceil(currentWidth / textMaxWidth) * (fontWidth * lineHeight)
  }, 0))
}